import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, OnChanges, SimpleChanges, Optional, Self, DoCheck } from '@angular/core';
import { ControlValueAccessor, NgControl, FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-custom-dropdown',
  templateUrl: './custom-dropdown.component.html',
  styleUrls: ['./custom-dropdown.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule
  ]
})
export class CustomDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, DoCheck {
  @Input() items: any[] = [];
  @Input() displayField: string = 'name';
  @Input() valueField: string = 'key';
  @Input() keyField: string = '';
  @Input() iconField: string = 'icon';
  @Input() placeholder: string = 'Select an item';
  @Input() addItems: boolean = false;
  @Input() multiple: boolean = false;
  @Input() addItemLabel: string = 'Add new item (press Enter)';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() width: string = '100%';
  @Input() icon: string = '';
  @Input() label: string = '';
  @Input() required: boolean = false;
  @Input() allowNull: boolean = false;
  @Input() nullOptionText: string = 'None';
  @Input() showNone: boolean = false;
  
  @Output() itemAdded = new EventEmitter<string>();
  @Output() itemDeleted = new EventEmitter<string>();
  
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('searchInput') searchInput!: ElementRef;
  
  searchControl = new FormControl('');
  filteredItems: any[] = [];
  selectedItems: any[] = [];
  selectedItem: any = null;
  newItemValue: string = '';
  normalizedItems: any[] = [];
  isStringArray: boolean = false;
  pendingNewItemSelection: string = '';
  
  // Form validation states
  errorState: boolean = false;
  
  // ControlValueAccessor implementation
  private onChange: any = () => {};
  private onTouched: any = () => {};
  disabled: boolean = false;
  
  constructor(@Optional() @Self() public ngControl: NgControl) {
    // Important: This is the key to avoiding circular dependency
    if (this.ngControl) {
      // Setting the value accessor directly (instead of using providers)
      this.ngControl.valueAccessor = this;
    }
  }
  
  // Computed property to check if we should show icons
  get showIcon(): boolean {
    return !!this.icon || this.hasItemsWithIcons();
  }
  
  // Check if any items have icons
  private hasItemsWithIcons(): boolean {
    return this.normalizedItems.some(item => !!item[this.iconField]);
  }
  
  ngOnInit() {
    // Set default keyField if not provided
    if (!this.keyField) {
      this.keyField = this.valueField;
    }
    
    this.normalizeItems();
    this.filteredItems = [...this.normalizedItems];
    
    // If we have a string value, convert it to the full object
    if (this.selectedItem && (typeof this.selectedItem === 'string' || typeof this.selectedItem === 'number')) {
      this.findAndSelectItemByKeyOrValue(this.selectedItem);
    }
  }
  
  ngDoCheck() {
    // Update error state from the form control
    if (this.ngControl) {
      this.updateErrorState();
    }
  }
  
  updateErrorState() {
    const oldState = this.errorState;
    const control = this.ngControl.control;
    const parent = this.ngControl.control?.parent;
    const newState = !!(control && control.invalid && (control.touched || (parent && (parent as any).submitted)));
    
    if (oldState !== newState) {
      this.errorState = newState;
    }
  }
  
  isRequired(): boolean {
    if (this.required) {
      return true;
    }
    
    if (this.ngControl && this.ngControl.control?.validator) {
      const validator = this.ngControl.control.validator({} as any);
      return !!(validator && validator['required']);
    }
    
    return false;
  }
  
  ngOnChanges(changes: SimpleChanges) {
    // Update keyField if not provided
    if (!this.keyField) {
      this.keyField = this.valueField;
    }
    
    // Update filtered items when the items input changes
    if (changes['items']) {
      this.normalizeItems();
      this.filteredItems = [...this.normalizedItems];
      this.onSearch(); // Apply any current search filter
      
      // If we have a value but no selected item, try to find it in the new items list
      if (!this.multiple) {
        // If we have a value from the ngControl but no selected item
        if (!this.selectedItem && this.ngControl?.value !== null && this.ngControl?.value !== undefined) {
          this.findAndSelectItemByKeyOrValue(this.ngControl.value);
        }
        // If we have a selected item that's a primitive, find the full object
        else if (this.selectedItem && (typeof this.selectedItem === 'string' || typeof this.selectedItem === 'number')) {
          this.findAndSelectItemByKeyOrValue(this.selectedItem);
        }
      }
      
      // If we have a pending new item selection, find and select it
      if (this.pendingNewItemSelection && this.multiple) {
        const newItem = this.normalizedItems.find(item => 
          item[this.displayField] === this.pendingNewItemSelection
        );
        
        if (newItem) {
          // Add the new item to the selected items
          if (!this.selectedItems.some(item => item[this.valueField] === newItem[this.valueField])) {
            this.selectedItems = [...this.selectedItems, newItem];
            this.onChange(this.selectedItems.filter(i => i !== null && i !== undefined).map(i => this.getItemValue(i)));
          }
          
          // Clear the pending selection
          this.pendingNewItemSelection = '';
        }
      }
    }
  }
  
  /**
   * Normalize items to handle both arrays of strings and arrays of objects
   */
  private normalizeItems(): void {
    if (!this.items || this.items.length === 0) {
      this.normalizedItems = [];
      return;
    }
    
    // Determine if we're dealing with a string array
    this.isStringArray = this.items.length > 0 && 
      (typeof this.items[0] === 'string' || 
       (this.items[0] && typeof this.items[0].toString === 'function' && !this.items[0].hasOwnProperty(this.valueField)));
    
    if (this.isStringArray) {
      // Convert strings/primitive values to objects with key and name properties
      this.normalizedItems = this.items.map((item, index) => {
        const stringValue = item?.toString() || '';
        return {
          [this.valueField]: stringValue,
          [this.displayField]: stringValue,
          [this.iconField]: null,
          _originalStringValue: stringValue
        };
      });
    } else {
      // Items are objects
      this.normalizedItems = this.items.map((item, index) => {
        if (!item) {
          // Handle null/undefined items
          return {
            [this.valueField]: `item_${index}`,
            [this.displayField]: '',
            [this.iconField]: null,
            _originalItem: null
          };
        }

        const itemValue = item[this.keyField] !== undefined && item[this.keyField] !== '' 
          ? item[this.keyField] 
          : `item_${index}`;
          
        return {
          [this.valueField]: item[this.valueField] !== undefined ? item[this.valueField] : itemValue,
          [this.displayField]: item[this.displayField] !== undefined ? item[this.displayField] : '',
          [this.iconField]: item[this.iconField] !== undefined ? item[this.iconField] : null,
          _originalItem: item
        };
      });
    }
    
    // Add the null option at the top if allowNull or showNone is true
    if ((this.allowNull || this.showNone) && !this.multiple) {
      const nullOption = {
        [this.valueField]: null,
        [this.displayField]: this.nullOptionText,
        [this.iconField]: null,
        _isNullOption: true
      };
      this.normalizedItems = [nullOption, ...this.normalizedItems];
    }
  }
  
  writeValue(value: any): void {
    console.log('writeValue called with:', value);
    console.log('Current items:', this.items);
    
    // Ensure items are normalized first
    if (this.items.length > 0 && this.normalizedItems.length === 0) {
      this.normalizeItems();
    }
    
    console.log('isStringArray:', this.isStringArray);
    console.log('normalizedItems:', this.normalizedItems);
    
    if (this.multiple) {
      if (!value) {
        this.selectedItems = [];
      } else if (this.isStringArray || (Array.isArray(value) && typeof value[0] === 'string')) {
        console.log('Converting string array values to normalized items');
        // For string arrays in multiple mode, convert the string values to normalized items
        this.selectedItems = value.map((v: string) => {
          const normalizedItem = this.normalizedItems.find(item => 
            this.isStringArray ? item._originalStringValue === v : item[this.valueField] === v
          );
          console.log('Looking for:', v, 'Found:', normalizedItem);
          return normalizedItem;
        }).filter((item: any) => item !== null && item !== undefined);
        console.log('Selected items after conversion:', this.selectedItems);
      } else {
        this.selectedItems = value;
      }
    } else {
      if (value === null || value === undefined) {
        this.selectedItem = null;
      } else if (typeof value === 'string' || typeof value === 'number') {
        // If value is a string or number, find the matching item
        const foundItem = this.findAndSelectItemByKeyOrValue(value);
        
        // If we couldn't find the item, it might be because the items list isn't ready yet
        // Store the value so we can try again when items are available
        if (!foundItem && this.items.length === 0) {
          // Store the value for later use when items are loaded
          setTimeout(() => {
            if (this.items.length > 0 && !this.selectedItem) {
              this.findAndSelectItemByKeyOrValue(value);
            }
          }, 100);
        } else if (!foundItem) {
          // If items are available but we still didn't find a match,
          // try one more time after a brief delay in case items are still being processed
          setTimeout(() => {
            if (!this.selectedItem) {
              this.findAndSelectItemByKeyOrValue(value);
            }
          }, 100);
        }
      } else {
        // Value is an object
        this.selectedItem = value;
      }
    }
  }
  
  /**
   * Find and select an item by its key or string value
   * Returns the found item or null
   */
  private findAndSelectItemByKeyOrValue(value: string | number): any {
    // First normalize the items if they haven't been normalized yet
    if (this.normalizedItems.length === 0 && this.items.length > 0) {
      this.normalizeItems();
    }
    
    // Try to find an item with a matching key
    const foundItem = this.normalizedItems.find(item => 
      item[this.valueField] === value.toString()
    );
    
    if (foundItem) {
      this.selectedItem = foundItem;
      return foundItem;
    } else if (this.isStringArray) {
      // If items are strings, try to find a matching string value
      const stringIndex = this.items.findIndex(item => item === value);
      if (stringIndex >= 0 && stringIndex < this.normalizedItems.length) {
        this.selectedItem = this.normalizedItems[stringIndex];
        return this.normalizedItems[stringIndex];
      } else {
        this.selectedItem = null;
        return null;
      }
    } else {
      // Try to find an item where the valueField matches the provided value
      // This handles the case where valueField is different from the keyField
      const valueFoundItem = this.normalizedItems.find(item => 
        item[this.valueField] === value ||
        item[this.valueField] == value ||  // Use loose equality for number/string comparison
        (item._originalItem && item._originalItem[this.valueField] === value)
      );
      
      if (valueFoundItem) {
        this.selectedItem = valueFoundItem;
        return valueFoundItem;
      }
      
      this.selectedItem = null;
      return null;
    }
  }
  
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  
  onItemSelect(item: any, event?: Event): void {
    if (this.multiple && event) {
      // For multiple selection with click event, prevent default behavior
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (this.multiple) {
      const index = this.selectedItems.findIndex(i => 
        i && (this.isStringArray 
          ? i._originalStringValue === item._originalStringValue
          : i[this.valueField] === item[this.valueField])
      );
      
      if (index === -1) {
        this.selectedItems = [...this.selectedItems, item];
      } else {
        this.selectedItems = this.selectedItems.filter(i => 
          i && (this.isStringArray 
            ? i._originalStringValue !== item._originalStringValue
            : i[this.valueField] !== item[this.valueField])
        );
      }
      // Return array of key values for multiple selection
      this.onChange(this.selectedItems.filter(i => i !== null && i !== undefined).map(i => this.getItemValue(i)));
      // Don't close menu for multiple selection
    } else {
      this.selectedItem = item;
      
      // Return null if the null option was selected
      if (item._isNullOption) {
        this.onChange(null);
      } else {
        // Return the appropriate value based on the item type
        this.onChange(this.getItemValue(item));
      }
      
      this.menuTrigger.closeMenu();
    }
    this.onTouched();
  }
  
  /**
   * Get the appropriate value to return based on the item type
   */
  private getItemValue(item: any): any {
    if (item._isNullOption) {
      return null;
    } else if (this.isStringArray && item._originalStringValue) {
      // For string arrays, return the original string
      return item._originalStringValue;
    } else if (item._originalItem) {
      // For object arrays, return the key value
      return item._originalItem[this.keyField];
    } else {
      // Fallback to the value field
      return item[this.valueField];
    }
  }
  
  isSelected(item: any): boolean {
    if (!item) return false;
    
    if (this.multiple) {
      if (!this.selectedItems || !Array.isArray(this.selectedItems)) return false;
      
      return this.selectedItems.some(selectedItem => {
        if (!selectedItem || !item) return false;
        
        if (this.isStringArray) {
          const selectedValue = selectedItem._originalStringValue || selectedItem[this.valueField];
          const itemValue = item._originalStringValue || item[this.valueField];
          return selectedValue === itemValue;
        } else {
          const selectedValue = selectedItem[this.valueField];
          const itemValue = item[this.valueField];
          return selectedValue === itemValue;
        }
      });
    } else {
      if (!this.selectedItem) return false;
      
      if (this.isStringArray) {
        const selectedValue = this.selectedItem._originalStringValue || this.selectedItem[this.valueField];
        const itemValue = item._originalStringValue || item[this.valueField];
        return selectedValue === itemValue;
      } else {
        const selectedValue = this.selectedItem[this.valueField];
        const itemValue = item[this.valueField];
        return selectedValue === itemValue || selectedValue == itemValue;
      }
    }
  }
  
  getDisplayValue(): string {
    if (this.multiple) {
      if (this.selectedItems.length === 0) {
        return this.placeholder;
      } else if (this.selectedItems.length === 1) {
        // Add null check to prevent TypeError
        const firstItem = this.selectedItems[0];
        return firstItem && firstItem[this.displayField] ? firstItem[this.displayField] : this.placeholder;
      } else {
        return `${this.selectedItems.length} items selected`;
      }
    } else {
      return this.selectedItem ? this.selectedItem[this.displayField] : this.placeholder;
    }
  }
  
  /**
   * Check if the dropdown has a selected value
   */
  hasValue(): boolean {
    if (this.multiple) {
      return this.selectedItems && this.selectedItems.length > 0;
    } else {
      return this.selectedItem !== null && this.selectedItem !== undefined && !this.selectedItem._isNullOption;
    }
  }
  
  onMenuOpened(): void {
    this.searchControl.setValue('');
    this.filteredItems = [...this.normalizedItems];
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    });
  }
  
  onSearch(): void {
    const searchTerm = this.searchControl.value?.toLowerCase() || '';
    
    // If we have a pending new item selection, make sure the search term doesn't filter it out
    if (this.pendingNewItemSelection && searchTerm) {
      // Clear the search term if it would filter out the pending new item
      if (!this.pendingNewItemSelection.toLowerCase().includes(searchTerm)) {
        this.searchControl.setValue('');
        this.filteredItems = [...this.normalizedItems];
        return;
      }
    }
    
    this.filteredItems = this.normalizedItems.filter(item => 
      item[this.displayField].toLowerCase().includes(searchTerm)
    );
  }
  
  /**
   * Find an item that matches the search term exactly or is the only partial match
   */
  private findBestMatch(searchTerm: string): any | null {
    if (!searchTerm) return null;
    
    const normalizedSearch = searchTerm.toLowerCase().trim();
    
    // First try to find an exact match
    const exactMatch = this.normalizedItems.find(item => 
      item[this.displayField].toLowerCase() === normalizedSearch
    );
    
    if (exactMatch) return exactMatch;
    
    // If no exact match, look for partial matches
    const partialMatches = this.normalizedItems.filter(item => 
      item[this.displayField].toLowerCase().includes(normalizedSearch)
    );
    
    // If there's exactly one partial match, use it
    if (partialMatches.length === 1) {
      return partialMatches[0];
    }
    
    // If there are multiple partial matches but one starts with the search term, use it
    const startsWithMatches = partialMatches.filter(item => 
      item[this.displayField].toLowerCase().startsWith(normalizedSearch)
    );
    
    if (startsWithMatches.length === 1) {
      return startsWithMatches[0];
    }
    
    return null;
  }

  addNewItem(): void {
    if (!this.newItemValue.trim()) return;

    // Try to find a best match first
    const bestMatch = this.findBestMatch(this.newItemValue);
    
    if (bestMatch) {
      // If we found a match, select it instead of creating a new item
      if (this.multiple) {
        if (!this.selectedItems.some(item => item[this.valueField] === bestMatch[this.valueField])) {
          this.selectedItems = [...this.selectedItems, bestMatch];
          this.onChange(this.selectedItems.filter(i => i !== null && i !== undefined).map(i => this.getItemValue(i)));
        }
      } else {
        this.selectedItem = bestMatch;
        this.onChange(this.getItemValue(bestMatch));
        this.menuTrigger.closeMenu();
      }
    } else {
      // No match found, create new item
      this.itemAdded.emit(this.newItemValue);
      this.pendingNewItemSelection = this.newItemValue;
    }

    // Clear the input field
    this.newItemValue = '';
    
    // Focus the search input
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    });
  }
  
  onNewItemInput(event: Event): void {
    this.newItemValue = (event.target as HTMLInputElement).value;
  }
  
  clearSelection(): void {
    if (this.multiple) {
      this.selectedItems = [];
    } else {
      this.selectedItem = null;
    }
    this.onChange(this.multiple ? [] : null);
    this.onTouched();
  }
  
  onMenuClosed(): void {
    // This method is called when the menu is closed
    // We can use it to perform any cleanup or additional actions
    this.onTouched();
  }

  /**
   * Remove an item from the items list and emit deletion event
   */
  removeFromDatabase(item: any): void {
    // Emit the value to be deleted
    const valueToDelete = this.isStringArray ? item._originalStringValue : this.getItemValue(item);
    this.itemDeleted.emit(valueToDelete);
    
    // Remove from items array
    this.items = this.items.filter(i => {
      if (this.isStringArray) {
        return i !== item._originalStringValue;
      } else {
        return this.getItemValue(i) !== this.getItemValue(item);
      }
    });
    
    // Remove from normalized items
    this.normalizedItems = this.normalizedItems.filter(i => {
      if (this.isStringArray) {
        return i._originalStringValue !== item._originalStringValue;
      } else {
        return this.getItemValue(i) !== this.getItemValue(item);
      }
    });
    
    // Remove from filtered items if present
    this.filteredItems = this.filteredItems.filter(i => {
      if (this.isStringArray) {
        return i._originalStringValue !== item._originalStringValue;
      } else {
        return this.getItemValue(i) !== this.getItemValue(item);
      }
    });
    
    // Remove from selected items if present
    if (this.multiple) {
      this.selectedItems = this.selectedItems.filter(i => {
        if (this.isStringArray) {
          return i._originalStringValue !== item._originalStringValue;
        } else {
          return this.getItemValue(i) !== this.getItemValue(item);
        }
      });
      this.onChange(this.selectedItems.filter(i => i !== null && i !== undefined).map(i => this.getItemValue(i)));
    } else if (this.selectedItem && this.getItemValue(this.selectedItem) === this.getItemValue(item)) {
      this.selectedItem = null;
      this.onChange(null);
    }
  }
} 